import {
  PageContainer,
  DefaultPageTemplate,
} from '@tkww/orion-web-ds-page-layout';
import {compose} from '@tkww/orion-web-lib-css-modules';
import React, {useContext, useEffect, useState, useRef} from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';
import {useLocation, NavLink} from 'react-router-dom';
import Honeybadger from '@honeybadger-io/js';
import get from 'lodash/get';
import ConversationsClient from '@xo-union/sdk-conversations';
import {
  GlobalHeaderPagelet,
  GlobalFooterPagelet,
} from '@tkww/orion-web-global-pagelets';
import Navigation, {
  SHOP_DESTINATION_NAV_OVERRIDES,
} from '@tkww/reg-shared-ui-navigation';
import useWindowDimensions from '../../hooks/useWindowDimensions';
import useIsMobileApp from '../../hooks/useIsMobileApp';
import {productsSearched} from '../../analytics';
import MemberContext from '../../contexts/MemberContext';
import trackSegment from '../../views/Checklist/utils/trackSegment';
import style from './style.scss';

const conversationsClient = new ConversationsClient();
const REGISTRY_NAVIGATION_INTERACTION_EVENT_NAME =
  'Registry Navigation Interaction';
const NON_PLP_NAVIGATION_ITEMS = ['Add Store Registries', 'Cash Funds'];
// Must mirror EXPERIMENTS.shopByInterest in @tkww/reg-shared-ui-navigation:
// the nav looks the passed assignment up by its own baked-in experiment id,
// and a mismatch leaves it waiting on its 5s fallback timer before rendering.
const SHOP_BY_INTEREST_V1_0_EXPERIMENT_ID =
  process.env.ENV !== 'production'
    ? '7bb995b8-9b18-4413-8c54-145f1c5daa2f'
    : 'c66ff30d-17cc-43b7-941c-1208170a50b8';
const SHOP_BY_INTEREST_V1_0_CONTROL_VARIANT_ID =
  process.env.ENV !== 'production'
    ? 'ab8565f1-dd93-4eda-bce1-60d8282bb175'
    : '46fce5ed-9a01-4b81-aeba-9bf099f2f507';

const fetchUnreadMessageCount = async memberId => {
  try {
    const response = await conversationsClient.getInboxUnreadMessagesCount({
      memberId,
    });

    return response.body.count || 0;
  } catch (error) {
    Honeybadger.notify(error, 'UnreadConversationsError');

    return null;
  }
};

const MenuItem = ({href, ...props}) => {
  const ownedRoutes = /shop|overview|checklist|manage-registry|track-gifts|settings|discover/;

  if (ownedRoutes && ownedRoutes.test(href)) {
    return <NavLink to={href.split('/registry').pop()} {...props} />;
  }

  // eslint-disable-next-line jsx-a11y/anchor-has-content
  return <a href={href} {...props} />;
};

MenuItem.propTypes = {
  href: PropTypes.string.isRequired,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
  ]).isRequired,
};

const trackRegistryNavigationInteraction = (
  category,
  additionalEventFields = {},
) => {
  const {plpType, ...rest} = additionalEventFields;
  const isNonPlpNavigationItem = NON_PLP_NAVIGATION_ITEMS.includes(category);
  const hasPlpType = !isNonPlpNavigationItem && plpType;

  trackSegment({
    eventName: REGISTRY_NAVIGATION_INTERACTION_EVENT_NAME,
    product: 'your registry',
    source: 'subnav',
    categoryName: category || null,
    plpType: !hasPlpType ? null : plpType,
    ...rest,
  });
};

const onMenuItemHover = ({category}) => {
  if (category !== 'Collections') return;

  trackRegistryNavigationInteraction(category, {
    selection: 'view collections dropdown',
    plpType: null,
  });
};
const onMenuItemClick = ({category, plpType}) => {
  trackRegistryNavigationInteraction(category, {
    plpType,
    selection: 'subnav click',
  });
};

const AppLayout = ({children}) => {
  const [minHeight, setMinHeight] = useState(null);
  const [unreadMessagesCount, setUnreadMessageCount] = useState(0);
  const containerRef = useRef();

  const {height} = useWindowDimensions();
  const {logOut, member, memberId} = useContext(MemberContext);
  const isMobileApp = useIsMobileApp();

  const location = useLocation();
  const isOnboarding = location.pathname.includes('/onboarding');
  const isOnboardingAddGifts = location.pathname === '/onboarding/add-gifts';
  const isCashTransferRoute = [
    '/cash/transfer',
    '/cash/transfer/bank-details',
  ].includes(location.pathname);
  const shouldShowHeaderAndFooter =
    !isOnboarding && !isMobileApp && !isCashTransferRoute;

  useEffect(() => {
    (async () => {
      if (memberId) {
        const unreadCount = await fetchUnreadMessageCount(memberId);

        if (typeof unreadCount === 'number') {
          setUnreadMessageCount(unreadCount);
        }
      }
    })();
  }, [memberId]);

  useEffect(() => {
    const offsetTop = get(containerRef, 'current.offsetTop');
    if (typeof offsetTop === 'number') {
      setMinHeight(height - offsetTop);
    }
  }, [height, containerRef]);

  const onSearchSubmit = ({searchTerm}) => {
    trackSegment({
      selection: 'view search result',
      children: null,
      href: location.pathname,
      searchTerm,
      plpType: 'search results',
    });
    productsSearched({
      query: searchTerm,
    });
  };

  return (
    <DefaultPageTemplate
      header={
        shouldShowHeaderAndFooter && (
          <GlobalHeaderPagelet
            forwardProps={{
              analyticsProps: {product: 'registry'},
              couplePhotoUrl: member.couple_photo_url,
              loggedIn: true,
              metadata: {applicationName: 'registry'},
              activeProduct: 'registry',
              onClickLogOut: logOut,
              unreadMessagesCount,
            }}
          />
        )
      }
      footer={
        shouldShowHeaderAndFooter && (
          <GlobalFooterPagelet
            forwardProps={{analyticsProps: {product: 'registry'}}}
          />
        )
      }>
      {shouldShowHeaderAndFooter && (
        <Navigation
          memberId={memberId}
          subMenuChildrenItemsStatus={{discover: true}}
          subMenuItemOverrides={SHOP_DESTINATION_NAV_OVERRIDES}
          onSearchSubmit={onSearchSubmit}
          onMenuItemHover={onMenuItemHover}
          onMenuItemClick={onMenuItemClick}
          MenuItemComponent={MenuItem}
          applicationName="member-registry"
          useCollectionsConceptualGrouping
          /**
           * required prop so we don't have to
           * wait for the 5s fallback timer and
           * wait for the sub nav to load (this'll
           * be removed once we update the shared nav)
           */
          experiments={{
            [SHOP_BY_INTEREST_V1_0_EXPERIMENT_ID]: {
              assignment: SHOP_BY_INTEREST_V1_0_CONTROL_VARIANT_ID,
              isLoading: false,
            },
          }}
        />
      )}
      <div className={style['your-registry']} ref={containerRef}>
        <PageContainer
          classes={compose({
            'page-container': cx(style['top-level-container'], {
              [style['no-padding-in-desktop']]:
                !isOnboardingAddGifts && isOnboarding,
            }),
          })}
          style={{minHeight}}>
          {children}
        </PageContainer>
      </div>
    </DefaultPageTemplate>
  );
};

AppLayout.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.element),
    PropTypes.element,
  ]).isRequired,
};

export default AppLayout;
